import { map } from "@suse-o11y/map";
import { useComputed } from "@suse-o11y/signal";
import { splitAt } from "@suse-o11y/split-at";
import React from "react";

import type { DashboardVariable } from "../../../../common/dashboard-spec";
import { DropdownFilter } from "../../../../components/_dropdown-filter";
import { ButtonGroup } from "../../../../components/react/buttons/Button";
import { useDashboardContext } from "../_dashboard-context";

import type { FormState } from "./_form-state";
import { Variable } from "./_variable";
import { Wrapper } from "./_wrapper";

interface Props {
	state: FormState;
}

const MAX_VISIBLE_VARIABLES = 3;

export const Variables = ({ state }: Props): React.JSX.Element => {
	const { variables } = useDashboardContext();

	const [visibleVariables, hiddenVariables] = useComputed(
		(monitor) => {
			const initialVariables = new Map(state.getInitial(monitor).map((variable) => [variable.name, variable]));

			const allVariables = variables.read(monitor).map((variable) => ({
				initialVariable: initialVariables.get(variable.name) ?? variable,
				variable,
			}));

			if (allVariables.length <= MAX_VISIBLE_VARIABLES) {
				return [allVariables, []];
			}

			return splitAt(allVariables, MAX_VISIBLE_VARIABLES - 1);
		},
		[variables, state],
	);

	const onChange = (variable: DashboardVariable) => {
		variables.write(map((current) => (current.name === variable.name ? variable : current)));
	};

	return (
		<ButtonGroup>
			{visibleVariables.map((variableState, index) => (
				<Wrapper key={index} variable={variableState.variable}>
					<Variable {...variableState} onChange={onChange} />
				</Wrapper>
			))}

			{hiddenVariables.length > 0 && (
				<DropdownFilter
					align={{
						targetOffset: [0, 4],
						overflow: { adjustX: false, adjustY: false },
					}}
					destroyPopupOnHide={false}
					label="More"
					overlay={() => (
						<div className="space-y-4">
							{hiddenVariables.map((variableState, index) => (
								<div key={index}>
									<Variable {...variableState} onChange={onChange} />
								</div>
							))}
						</div>
					)}
					placement="bottomRight"
					selectedLabel={undefined}
				/>
			)}
		</ButtonGroup>
	);
};
